/**
 * Lønn og kontrakt fra PowerOffice (#183 phase 2): for each person whose pay or contract in Timefly
 * differs from PowerOffice Go (rate, monthly pay, start, end, form, agreed week), the two side by
 * side. The administrator ticks what to take from PowerOffice; the server reads Go again and writes
 * only those fields. Nothing changes before Lagre.
 */
import { errorText, get, put } from '@/api/client';
import { T } from '@/i18n';
import { Button, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { Notice } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EMPLOYMENT_FORMS } from '../ansatt/roster';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';

type Field =
  | 'contract_type'
  | 'hourly_rate'
  | 'monthly_rate'
  | 'employment_start'
  | 'end_date'
  | 'employment_form'
  | 'agreed_week_hours';
interface Diff {
  user_id: number;
  name: string;
  number: string;
  /** the system's value is under its key: `poweroffice` or `tripletex` (PayrollSystem.key) */
  changes: ({ field: Field; timefly: string | number | null } & Partial<
    Record<PayrollSystem['key'], string | number>
  >)[];
  /** the person's active fixed salary lines in Go: Go pays them each payroll, Timefly only shows them */
  fixed?: {
    code: string | null;
    name: string | null;
    amount: number | null;
    rate: number | null;
    quantity: number | null;
    comment: string | null;
  }[];
  /** cost and billable rate per hour in Go (#183 phase 3): for projects, not pay; shown, never taken */
  hour_rates?: { cost: number | null; billable: number | null; from: string | null };
}
const LABEL: Record<Field, string> = {
  contract_type: 'Lønnsform',
  hourly_rate: 'Timelønn',
  monthly_rate: 'Månedslønn',
  employment_start: 'Startdato',
  end_date: 'Sluttdato',
  employment_form: 'Ansettelsesform',
  agreed_week_hours: 'Avtalt uke',
};
const kr = (v: number) =>
  `${v.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
function shown(f: Field, v: string | number | null): string {
  if (v == null || v === '') return '–';
  if (f === 'hourly_rate' || f === 'monthly_rate') return kr(Number(v));
  if (f === 'agreed_week_hours') return `${String(v).replace('.', ',')} t`;
  if (f === 'employment_start' || f === 'end_date') return fmtD(String(v).slice(0, 10));
  if (f === 'employment_form') return T(EMPLOYMENT_FORMS[v as keyof typeof EMPLOYMENT_FORMS] ?? String(v));
  if (f === 'contract_type') return v === 'monthly' ? T('Fast månedslønn') : T('Timelønn');
  return String(v);
}

export function PowerOfficeContracts({
  onClose,
  sys = POWEROFFICE,
}: { onClose: () => void; sys?: PayrollSystem }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [sys.key, 'contracts'],
    queryFn: () => get<{ people: Diff[] }>(`${sys.base}/contracts`),
    staleTime: 0,
  });
  const [pick, setPick] = useState<Record<number, Set<Field>>>({});
  const save = useMutation({
    mutationFn: () =>
      put<{ people: Diff[] }>(`${sys.base}/contracts`, {
        people: Object.entries(pick)
          .filter(([, f]) => f.size)
          .map(([user_id, f]) => ({ user_id: Number(user_id), fields: [...f] })),
      }),
    onSuccess: (r) => {
      qc.setQueryData([sys.key, 'contracts'], r);
      qc.invalidateQueries({ queryKey: ['users'] });
      setPick({});
      toast(T('Lønn og kontrakt er oppdatert fra {system}.', { system: sys.short }));
    },
    onError: (e) => toast(errorText(e)),
  });
  const toggle = (u: number, f: Field, on: boolean) => {
    const next = new Set(pick[u] ?? []);
    if (on) next.add(f);
    else next.delete(f);
    setPick({ ...pick, [u]: next });
  };
  const n = Object.values(pick).reduce((a, f) => a + f.size, 0);
  const people = q.data?.people ?? [];
  const withFixed = people.filter((p) => p.fixed?.length);
  const withRates = people.filter((p) => p.hour_rates);
  const changing = people.filter((p) => p.changes.length);
  return (
    <section data-testid="po-contracts" style={{ display: 'grid', gap: 12, marginTop: 6 }}>
      <div>
        <b style={{ fontSize: 15 }}>{T('Lønn og kontrakt fra {system}', { system: sys.short })}</b>
        <p style={{ margin: '4px 0 0', color: 'var(--ink3)', fontSize: 13 }}>
          {T(
            'Her er de som har en annen lønn eller kontrakt i Timefly enn i PowerOffice. Kryss av det du vil hente fra PowerOffice og trykk Lagre.',
          )}
        </p>
      </div>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter lønn og kontrakter fra {system} …', { system: sys.short })}
        </output>
      ) : q.isError ? (
        <Notice tone="dgr" title={T('Kunne ikke hente fra {system}', { system: sys.short })}>
          {errorText(q.error)}
        </Notice>
      ) : changing.length === 0 ? (
        <p style={{ margin: 0 }} data-testid="po-contracts-none">
          {T('Lønn og kontrakt er lik i Timefly og {system} for alle som er koblet.', { system: sys.short })}
        </p>
      ) : (
        <table className="t">
          <thead>
            <tr>
              <th>{T('Ansatt')}</th>
              <th>{T('Felt')}</th>
              <th>{T('I Timefly')}</th>
              <th>{T('I {system}', { system: sys.short })}</th>
              <th>{T('Hent')}</th>
            </tr>
          </thead>
          <tbody>
            {changing.flatMap((p) =>
              p.changes.map((c, i) => (
                <tr key={`${p.user_id}-${c.field}`}>
                  <td>{i === 0 ? `${p.name} (${p.number})` : ''}</td>
                  <td>{T(LABEL[c.field])}</td>
                  <td>{shown(c.field, c.timefly)}</td>
                  <td>{shown(c.field, c[sys.key] ?? null)}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={T('Hent {field} for {name}', { field: T(LABEL[c.field]), name: p.name })}
                      checked={pick[p.user_id]?.has(c.field) ?? false}
                      onChange={(e) => toggle(p.user_id, c.field, e.target.checked)}
                    />
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
      {withFixed.length > 0 && (
        <div data-testid="po-fixed" style={{ display: 'grid', gap: 4 }}>
          <b style={{ fontSize: 14 }}>{T('Faste lønnslinjer i {system}', { system: sys.short })}</b>
          <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
            {T(
              'PowerOffice legger dem til hver lønnskjøring selv. Timefly sender dem ikke, så de blir ikke betalt to ganger.',
            )}
          </span>
          {withFixed.map((p) => (
            <span key={p.user_id} style={{ fontSize: 14 }}>
              {p.name} ({p.number}):{' '}
              {(p.fixed ?? [])
                .map(
                  (x) =>
                    `${[x.code, x.name].filter(Boolean).join(' ')} ${
                      x.amount != null
                        ? kr(x.amount)
                        : x.rate != null
                          ? `${x.quantity ?? ''} × ${kr(x.rate)}`
                          : ''
                    }${x.comment ? ` (${x.comment})` : ''}`,
                )
                .join(', ')}
            </span>
          ))}
        </div>
      )}
      {withRates.length > 0 && (
        <div data-testid="po-hour-rates" style={{ display: 'grid', gap: 4 }}>
          <b style={{ fontSize: 14 }}>{T('Kostpris og timepris i {system}', { system: sys.short })}</b>
          <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
            {T(
              'Brukes til prosjekter og fakturering i PowerOffice. Det er ikke lønn, og Timefly endrer ikke timelønnen etter dem.',
            )}
          </span>
          {withRates.map((p) => (
            <span key={p.user_id} style={{ fontSize: 14 }}>
              {p.name} ({p.number}):{' '}
              {[
                p.hour_rates?.cost != null ? `${T('kostpris')} ${kr(p.hour_rates.cost)}` : null,
                p.hour_rates?.billable != null ? `${T('timepris')} ${kr(p.hour_rates.billable)}` : null,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          ))}
        </div>
      )}
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button tone="pri" onClick={() => save.mutate()} disabled={save.isPending || n === 0}>
          {save.isPending ? T('Lagrer…') : n ? T('Hent {n} verdier', { n }) : T('Ingen valgt')}
        </Button>
        <Button onClick={onClose} disabled={save.isPending}>
          {T('Lukk')}
        </Button>
      </span>
    </section>
  );
}
